/**
 * Page of the inspection report as the PDF service lays it out (A4 in points, `pdfCreator`).
 *
 * The top band is the header row (14 pt text + 24 pt margin). The bottom band is the page counter margin
 * (48) plus the footer column holding the inspection code (16 pt top margin + 6 pt text). A project logo or
 * configured footer lines make both bands taller; the frontend does not know them, so the estimate assumes
 * the plain variant — the lower bound of the static assets.
 */
export const PDF_PAGE_HEIGHT = 841.89;
export const PDF_PAGE_CONTENT_TOP = 40.1;
export const PDF_PAGE_CONTENT_BOTTOM = PDF_PAGE_HEIGHT - 70.9;
export const PDF_PAGE_CONTENT_HEIGHT = PDF_PAGE_CONTENT_BOTTOM - PDF_PAGE_CONTENT_TOP;

/** A4 width (595.28) minus the 32 pt side margins. */
export const PDF_CONTENT_WIDTH = 531.28;

/** `minSplitRemainderRatio`: below this much room a block that does not fit moves to the next page. */
export const PDF_MIN_SPLIT_REMAINDER = 0.25 * PDF_PAGE_CONTENT_HEIGHT;

/** `staticMaxImageHeight` — the cap of a full-size picture. */
export const PDF_MAX_FULL_SIZE_IMAGE_HEIGHT = 550;

/**
 * `CalculateColumnX`: the 12-column grid, 45.25 pt per column with a 12 pt gutter. The backend positions
 * attachment blocks and miniatures on it, as shares (`0..1`) of the page width.
 */
const GRID_SPAN_WIDTH = 12 * 45.25;
const GRID_GUTTER = 12;
const GRID_PADDING_BASE = 531;

export function gridSpanWidth(widthRatio: number): number {
  return widthRatio * GRID_SPAN_WIDTH - GRID_GUTTER;
}

/** `CalculateColumnX.availableRatioInCell`: a cell's share of the grid minus its padding. */
export function availableRatioInCell(widthRatio: number, horizontalPadding: number): number {
  return widthRatio - horizontalPadding / GRID_PADDING_BASE;
}

/** Where the next item would be drawn: page number and y of its top edge. */
export class PdfPageCursor {
  constructor(
    public page = 1,
    public y = PDF_PAGE_CONTENT_TOP
  ) {}

  get available(): number {
    return PDF_PAGE_CONTENT_BOTTOM - this.y;
  }

  get isAtPageTop(): boolean {
    return this.y <= PDF_PAGE_CONTENT_TOP;
  }

  advancePage(): void {
    this.page += 1;
    this.y = PDF_PAGE_CONTENT_TOP;
  }

  advanceIfLessThan(height: number): void {
    if (this.available < height) {
      this.advancePage();
    }
  }

  clone(): PdfPageCursor {
    return new PdfPageCursor(this.page, this.y);
  }

  isBefore(other: PdfPageCursor): boolean {
    return this.page < other.page || (this.page === other.page && this.y < other.y);
  }

  moveTo(other: PdfPageCursor): void {
    this.page = other.page;
    this.y = other.y;
  }
}
